import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { guestSavedIds, mergeGuestSaves, notify, setGuestSaved, subscribe } from '../utils/savedProducts';
import './SaveButton.css';

const HEART = 'M12 20.5s-7.5-4.6-9.3-9.2C1.5 8.2 3.4 4.5 7 4.5c2 0 3.4 1.1 5 3 1.6-1.9 3-3 5-3 3.6 0 5.5 3.7 4.3 6.8-1.8 4.6-9.3 9.2-9.3 9.2z';

let merged = false;

const likeHeaders = () => {
  const headers = {};
  try {
    let hash = localStorage.getItem('session_hash');
    if (!hash && typeof crypto !== 'undefined' && crypto.randomUUID) {
      hash = crypto.randomUUID();
      localStorage.setItem('session_hash', hash);
    }
    if (hash) headers['X-Session-Hash'] = hash;
    const token = localStorage.getItem('customer_token');
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch { /* storage blocked */ }
  return headers;
};

// Saves double as the store's "most liked" signal: keep the product's like
// in step with the save, without making the shopper press two buttons.
const syncLike = async (productId, saved) => {
  try {
    const headers = likeHeaders();
    const { data } = await api.get(`/api/products/${productId}/like-status`, { headers });
    if (Boolean(data?.liked) !== saved) await api.post(`/api/products/${productId}/like`, {}, { headers });
  } catch { /* popularity is best effort */ }
};

/**
 * The one heart: save a product for later. Signed-in shoppers save to
 * their wishlist; guests save in this browser (moved to the account at
 * sign-in), so the heart works without asking anyone to log in first.
 */
export default function SaveButton({ productId, size = 'sm', className = '' }) {
  const { t } = useTranslation();
  const { isAuthenticated, loading: authLoading } = useCustomerAuth();
  const [saved, setSaved] = useState(false);
  const [pop, setPop] = useState(false);

  useEffect(() => {
    if (authLoading || productId == null) return undefined;
    let cancelled = false;
    if (isAuthenticated) {
      (async () => {
        if (!merged) { merged = true; await mergeGuestSaves(); }
        try {
          const { data } = await api.get(`/api/customer/wishlist/check/${productId}`);
          if (!cancelled) setSaved(Boolean(data?.inWishlist));
        } catch { if (!cancelled) setSaved(false); }
      })();
    } else {
      setSaved(guestSavedIds().includes(productId));
    }
    const unsubscribe = subscribe((id, value) => { if (id === productId) setSaved(value); });
    return () => { cancelled = true; unsubscribe(); };
  }, [authLoading, isAuthenticated, productId]);

  const toggle = useCallback(async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const next = !saved;
    setSaved(next);
    setPop(true);
    window.setTimeout(() => setPop(false), 220);
    try {
      if (isAuthenticated) {
        await (next ? api.post(`/api/customer/wishlist/${productId}`) : api.delete(`/api/customer/wishlist/${productId}`));
        notify(productId, next);
      } else {
        setGuestSaved(productId, next);
      }
      syncLike(productId, next);
    } catch {
      setSaved(!next);
    }
  }, [isAuthenticated, productId, saved]);

  return (
    <button
      type="button"
      className={`save-button save-button--${size}${saved ? ' is-saved' : ''}${pop ? ' is-popping' : ''} ${className}`}
      onClick={toggle}
      aria-pressed={saved}
      aria-label={saved ? t('products.unsave', 'Remove from saved') : t('products.save', 'Save for later')}
      title={saved ? t('products.unsave', 'Remove from saved') : t('products.save', 'Save for later')}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d={HEART} /></svg>
    </button>
  );
}
